import React, {useEffect, useState} from 'react';
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tab,
    Tabs,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useReportStore} from '../../features/products/useReportStore';
import {useSession} from '../../firebase/UserProvider';
import {subscribeToUserReports} from '../../firebase/firestore/reports';
import {API_BASE} from '../../utils/api/reports';
import buildHeaders from '../../utils/api/fetchHeaders';

/**
 * Fetch all folders for another product's source package (cross-product tab).
 * Directly calls the REST endpoint and extracts FOLDER type items.
 */
async function fetchOtherProductFolders(productId) {
    const params = new URLSearchParams({page: '0', size: '1'});
    const response = await fetch(`${API_BASE}/products/${productId}/source-package?${params}`, {
        method: 'GET',
        headers: buildHeaders({'Accept': 'application/json'}),
    });
    if (!response.ok) throw new Error('Failed to fetch product folders');
    const data = await response.json();
    const nodes = Array.isArray(data) ? data : (data.items || []);
    return nodes
        .filter(n => n.type === 'FOLDER')
        .map(n => ({
            id: String(n.id),
            name: n.entity?.name || n.entity?.title || 'Untitled',
            parentFolderId: n.parentFolderId != null ? String(n.parentFolderId) : null,
        }));
}

/**
 * Folder list item with hover "Move" button and double-click navigation
 */
function FolderListItem({
                            folder,
                            isSelected,
                            onSelect,
                            onNavigate,
                            onMoveHere,
                            truncateText,
                        }) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <ListItem
            disablePadding
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            secondaryAction={
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    {isHovered && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMoveHere(folder.id);
                            }}
                            sx={{minWidth: 'auto', px: 1.5, py: 0.25, fontSize: '0.75rem'}}
                        >
                            Move
                        </Button>
                    )}
                    <IconButton
                        edge="end"
                        onClick={(e) => {
                            e.stopPropagation();
                            onNavigate(folder);
                        }}
                        size="small"
                        sx={{opacity: isHovered ? 1 : 0.5}}
                    >
                        <ChevronRightIcon/>
                    </IconButton>
                </Box>
            }
        >
            <ListItemButton
                selected={isSelected}
                onClick={() => onSelect(folder.id)}
                onDoubleClick={() => onNavigate(folder)}
            >
                <ListItemIcon sx={{minWidth: 40}}>
                    <FolderIcon/>
                </ListItemIcon>
                <ListItemText primary={truncateText(folder.name || 'Untitled')}/>
            </ListItemButton>
        </ListItem>
    );
}

/**
 * Tabbed dialog for adding evidence to source package
 * Similar to Google Drive's "Move to" dialog
 */
export default function SourcePackageSubmenu({
                                                 open,
                                                 folderList,
                                                 onAddToRoot,
                                                 onAddToFolder,
                                                 onAddToAnotherProduct,
                                                 onCreateNewFolder,
                                                 onClose,
                                                 folderRefreshTrigger,
                                             }) {
    const [selectedTab, setSelectedTab] = useState(1);
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [folderPath, setFolderPath] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // State for other product's folder navigation
    const [otherProductFolders, setOtherProductFolders] = useState([]);
    const [otherProductCurrentFolderId, setOtherProductCurrentFolderId] = useState(null);
    const [otherProductFolderPath, setOtherProductFolderPath] = useState([]);
    const [loadingOtherProductFolders, setLoadingOtherProductFolders] = useState(false);

    const {user} = useSession();
    const currentReportId = useReportStore(s => s.id || s.reportId);
    const currentReportTitle = useReportStore(s => s.title);

    // Get recently used folders (last 3)
    const getRecentFolders = () => {
        return [...(folderList || [])].slice(-3).reverse();
    };

    // Load all user products for "Another Product" tab
    useEffect(() => {
        if (selectedTab === 2 && user?.uid) {
            setLoadingProducts(true);
            const unsubscribe = subscribeToUserReports(
                user.uid,
                (reports) => {
                    setAllProducts(reports.filter(r => r.id !== currentReportId));
                    setLoadingProducts(false);
                },
                (err) => {
                    console.error('Failed to load products:', err);
                    setLoadingProducts(false);
                }
            );
            return () => unsubscribe?.();
        }
    }, [selectedTab, user?.uid, currentReportId]);

    // Load selected product's folders
    useEffect(() => {
        if (selectedProductId) {
            loadOtherProductFolders(selectedProductId);
        } else {
            setOtherProductFolders([]);
            setOtherProductCurrentFolderId(null);
            setOtherProductFolderPath([]);
        }
    }, [selectedProductId]);

    // Function to load/reload other product's folders
    const loadOtherProductFolders = (productId) => {
        setLoadingOtherProductFolders(true);
        fetchOtherProductFolders(productId)
            .then(folders => {
                setOtherProductFolders(folders);
                setLoadingOtherProductFolders(false);
            })
            .catch(err => {
                console.error('Failed to load product folders:', err);
                setOtherProductFolders([]);
                setLoadingOtherProductFolders(false);
            });
    };

    // Refresh folders when trigger changes (after folder creation)
    useEffect(() => {
        if (folderRefreshTrigger > 0 && selectedProductId) {
            loadOtherProductFolders(selectedProductId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderRefreshTrigger, selectedProductId]);

    // Get subfolders of current folder
    const getCurrentSubfolders = () => {
        return (folderList || []).filter(f => f.parentFolderId === currentFolderId);
    };

    // Get subfolders of other product's current folder
    const getOtherProductSubfolders = () => {
        return (otherProductFolders || []).filter(f => f.parentFolderId === otherProductCurrentFolderId);
    };

    // Navigate into a folder in other product
    const handleOtherProductNavigateFolder = (folder) => {
        setOtherProductCurrentFolderId(folder.id);
        setOtherProductFolderPath([...otherProductFolderPath, folder]);
        setSelectedLocation(folder.id);
    };

    // Navigate to root in other product
    const handleOtherProductNavigateRoot = () => {
        setOtherProductCurrentFolderId(null);
        setOtherProductFolderPath([]);
        setSelectedLocation(null);
    };

    // Navigate via breadcrumb in other product
    const handleOtherProductBreadcrumbClick = (index) => {
        if (index === -1) {
            handleOtherProductNavigateRoot();
        } else {
            const newPath = otherProductFolderPath.slice(0, index + 1);
            const folder = newPath[newPath.length - 1];
            setOtherProductCurrentFolderId(folder.id);
            setOtherProductFolderPath(newPath);
            setSelectedLocation(folder.id);
        }
    };

    // Navigate into a folder
    const handleNavigateFolder = (folder) => {
        setCurrentFolderId(folder.id);
        setFolderPath([...folderPath, folder]);
        setSelectedLocation(folder.id);
    };

    // Navigate to root
    const handleNavigateRoot = () => {
        setCurrentFolderId(null);
        setFolderPath([]);
        setSelectedLocation(null);
    };

    // Navigate via breadcrumb
    const handleBreadcrumbClick = (index) => {
        if (index === -1) {
            handleNavigateRoot();
        } else {
            const newPath = folderPath.slice(0, index + 1);
            const folder = newPath[newPath.length - 1];
            setCurrentFolderId(folder.id);
            setFolderPath(newPath);
            setSelectedLocation(folder.id);
        }
    };

    // Handle move/add action
    const handleMove = () => {
        if (selectedTab === 0 || selectedTab === 1) {
            // Current product tabs
            if (selectedLocation === null) {
                onAddToRoot();
            } else {
                onAddToFolder(selectedLocation);
            }
        } else if (selectedTab === 2 && selectedProductId) {
            // Another product tab - cross-product move
            onAddToAnotherProduct?.(selectedProductId, selectedLocation);
        }
        handleClose();
    };

    const handleClose = () => {
        setSelectedTab(0);
        setCurrentFolderId(null);
        setFolderPath([]);
        setSelectedLocation(null);
        setSelectedProductId(null);
        setOtherProductFolders([]);
        setOtherProductCurrentFolderId(null);
        setOtherProductFolderPath([]);
        onClose();
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        setCurrentFolderId(null);
        setFolderPath([]);
        setSelectedLocation(null);
        // Reset other product state when switching tabs
        if (newValue !== 2) {
            setSelectedProductId(null);
            setOtherProductFolders([]);
            setOtherProductCurrentFolderId(null);
            setOtherProductFolderPath([]);
        }
    };

    const truncateText = (text, maxLength = 48) => {
        if (!text) return 'Untitled';
        return text.length > maxLength ? text.slice(0, maxLength - 3) + '…' : text;
    };

    // Render Suggested tab
    const renderSuggested = () => {
        const recentFolders = getRecentFolders();
        return (
            <List sx={{width: '100%', py: 0}}>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={selectedLocation === null}
                        onClick={() => setSelectedLocation(null)}
                    >
                        <ListItemIcon sx={{minWidth: 40}}>
                            <FolderIcon/>
                        </ListItemIcon>
                        <ListItemText
                            primary="Root (Top level)"
                            secondary={truncateText(currentReportTitle || 'Current product', 40)}
                        />
                    </ListItemButton>
                </ListItem>
                {recentFolders.map((folder) => (
                    <FolderListItem
                        key={folder.id}
                        folder={folder}
                        isSelected={selectedLocation === folder.id}
                        onSelect={setSelectedLocation}
                        onNavigate={handleNavigateFolder}
                        onMoveHere={(folderId) => {
                            onAddToFolder(folderId);
                            handleClose();
                        }}
                        truncateText={truncateText}
                    />
                ))}
                {recentFolders.length === 0 && (
                    <ListItem>
                        <ListItemText
                            secondary="No recently used folders"
                            sx={{fontStyle: 'italic'}}
                        />
                    </ListItem>
                )}
            </List>
        );
    };

    // Render Current Product tab with folder navigation
    const renderCurrentProduct = () => {
        const subfolders = getCurrentSubfolders();

        return (
            <Box sx={{width: '100%'}}>
                {/* Breadcrumbs */}
                <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small"/>}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleNavigateRoot}
                            sx={{cursor: 'pointer'}}
                            underline="hover"
                        >
                            {truncateText(currentReportTitle || 'Root', 25)}
                        </Link>
                        {folderPath.map((folder, index) => (
                            <Link
                                key={folder.id}
                                component="button"
                                variant="body2"
                                onClick={() => handleBreadcrumbClick(index)}
                                sx={{cursor: 'pointer'}}
                                underline="hover"
                            >
                                {truncateText(folder.name || 'Untitled', 25)}
                            </Link>
                        ))}
                    </Breadcrumbs>
                </Box>

                {/* Folder list */}
                <List sx={{py: 0}}>
                    {currentFolderId === null && (
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={selectedLocation === null}
                                onClick={() => setSelectedLocation(null)}
                            >
                                <ListItemIcon sx={{minWidth: 40}}>
                                    <FolderIcon color="primary"/>
                                </ListItemIcon>
                                <ListItemText primary="Root (Top level)"/>
                            </ListItemButton>
                        </ListItem>
                    )}

                    {subfolders.map((folder) => (
                        <FolderListItem
                            key={folder.id}
                            folder={folder}
                            isSelected={selectedLocation === folder.id}
                            onSelect={setSelectedLocation}
                            onNavigate={handleNavigateFolder}
                            onMoveHere={(folderId) => {
                                onAddToFolder(folderId);
                                handleClose();
                            }}
                            truncateText={truncateText}
                        />
                    ))}

                    {subfolders.length === 0 && currentFolderId !== null && (
                        <ListItem>
                            <ListItemText
                                secondary="No subfolders"
                                sx={{fontStyle: 'italic', pl: 2}}
                            />
                        </ListItem>
                    )}
                </List>
            </Box>
        );
    };

    // Render Another Product tab
    const renderAnotherProduct = () => {
        if (loadingProducts) {
            return (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            );
        }

        // If no product is selected, show the list of products
        if (!selectedProductId) {
            return (
                <List sx={{width: '100%', py: 0}}>
                    {allProducts.map((product) => (
                        <ListItem
                            key={product.id}
                            disablePadding
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedProductId(product.id);
                                        setSelectedLocation(null);
                                    }}
                                    size="small"
                                    sx={{opacity: 0.5}}
                                >
                                    <ChevronRightIcon/>
                                </IconButton>
                            }
                        >
                            <ListItemButton
                                onClick={() => {
                                    setSelectedProductId(product.id);
                                    setSelectedLocation(null);
                                }}
                            >
                                <ListItemIcon sx={{minWidth: 40}}>
                                    <FolderIcon/>
                                </ListItemIcon>
                                <ListItemText
                                    primary={truncateText(product.title || 'Untitled Product')}
                                    secondary={product.productType?.label || 'Product'}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {allProducts.length === 0 && (
                        <ListItem>
                            <ListItemText
                                secondary="No other products available"
                                sx={{fontStyle: 'italic'}}
                            />
                        </ListItem>
                    )}
                </List>
            );
        }

        // Product is selected - show its folder structure
        const selectedProduct = allProducts.find(p => p.id === selectedProductId);
        const subfolders = getOtherProductSubfolders();

        if (loadingOtherProductFolders) {
            return (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            );
        }

        return (
            <Box sx={{width: '100%'}}>
                {/* Breadcrumbs */}
                <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                    <Breadcrumbs separator={<ChevronRightIcon fontSize="small"/>}>
                        {/* Back to products link */}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => {
                                setSelectedProductId(null);
                                setOtherProductFolders([]);
                                setOtherProductCurrentFolderId(null);
                                setOtherProductFolderPath([]);
                                setSelectedLocation(null);
                            }}
                            sx={{cursor: 'pointer'}}
                            underline="hover"
                        >
                            Products
                        </Link>
                        {/* Selected product root */}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleOtherProductNavigateRoot}
                            sx={{cursor: 'pointer'}}
                            underline="hover"
                        >
                            {truncateText(selectedProduct?.title || 'Product', 25)}
                        </Link>
                        {otherProductFolderPath.map((folder, index) => (
                            <Link
                                key={folder.id}
                                component="button"
                                variant="body2"
                                onClick={() => handleOtherProductBreadcrumbClick(index)}
                                sx={{cursor: 'pointer'}}
                                underline="hover"
                            >
                                {truncateText(folder.name || 'Untitled', 25)}
                            </Link>
                        ))}
                    </Breadcrumbs>
                </Box>

                {/* Folder list */}
                <List sx={{py: 0}}>
                    {otherProductCurrentFolderId === null && (
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={selectedLocation === null}
                                onClick={() => setSelectedLocation(null)}
                            >
                                <ListItemIcon sx={{minWidth: 40}}>
                                    <FolderIcon color="primary"/>
                                </ListItemIcon>
                                <ListItemText primary="Root (Top level)"/>
                            </ListItemButton>
                        </ListItem>
                    )}

                    {subfolders.map((folder) => (
                        <FolderListItem
                            key={folder.id}
                            folder={folder}
                            isSelected={selectedLocation === folder.id}
                            onSelect={setSelectedLocation}
                            onNavigate={handleOtherProductNavigateFolder}
                            onMoveHere={(folderId) => {
                                // Cross-product move
                                onAddToAnotherProduct?.(selectedProductId, folderId);
                                handleClose();
                            }}
                            truncateText={truncateText}
                        />
                    ))}

                    {subfolders.length === 0 && otherProductCurrentFolderId !== null && (
                        <ListItem>
                            <ListItemText
                                secondary="No subfolders"
                                sx={{fontStyle: 'italic', pl: 2}}
                            />
                        </ListItem>
                    )}

                    {subfolders.length === 0 && otherProductCurrentFolderId === null && otherProductFolders.length === 0 && (
                        <ListItem>
                            <ListItemText
                                secondary="No folders in this product"
                                sx={{fontStyle: 'italic', pl: 2}}
                            />
                        </ListItem>
                    )}
                </List>
            </Box>
        );
    };

    const canMove = selectedTab !== 2 || selectedProductId;

    // New Folder is only available for:
    // - Current Product tab (tab 1)
    // - Another Product tab (tab 2) when a product is selected/navigated
    const canCreateFolder = selectedTab === 1 || (selectedTab === 2 && selectedProductId);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {sx: {minHeight: 450, height: '558px'}}
            }}
        >
            <DialogTitle sx={{p: 2, pb: 0.5, pt: 1.5}}>
                Add to Source Package
            </DialogTitle>

            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{px: 1, borderBottom: 1, borderColor: 'divider'}}
            >
                <Tab label="Suggested" sx={{textTransform: 'none'}}/>
                <Tab label="Current Product" sx={{textTransform: 'none'}}/>
                <Tab label="Another Product" sx={{textTransform: 'none'}}/>
            </Tabs>

            <DialogContent sx={{p: 0, minHeight: 250}}>
                {selectedTab === 0 && renderSuggested()}
                {selectedTab === 1 && renderCurrentProduct()}
                {selectedTab === 2 && renderAnotherProduct()}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                {canCreateFolder && (
                    <Button
                        onClick={() => {
                            // Pass the appropriate parent folder ID based on current tab
                            const parentFolderId = selectedTab === 2
                                ? otherProductCurrentFolderId
                                : currentFolderId;
                            // Pass product ID for cross-product folder creation
                            const productId = selectedTab === 2
                                ? selectedProductId
                                : null;
                            onCreateNewFolder(parentFolderId, productId);
                        }}
                        variant="outlined"
                        size="small"
                    >
                        New Folder
                    </Button>
                )}
                <Button
                    onClick={handleMove}
                    variant="contained"
                    disabled={!canMove}
                >
                    Move
                </Button>
            </DialogActions>
        </Dialog>
    );
}
